import { Link } from "react-router-dom";
import { Settings as SettingsIcon } from "lucide-react";

import "../styles/landing-luxury.css";
import "../styles/phase2-public.css";

import DocsCategoryLayout from "../components/docs/DocsCategoryLayout";
import { Steps, Tip, DocsTable } from "../components/docs/DocsBits";

const ARTICLES = [
  {
    id: "scheduling",
    title: "Scheduling & Calendar",
    body: (
      <>
        <p>The Jobs page is the central scheduling surface. Switch between list and calendar views with the toggle at the top right.</p>
        <h3>Drag-and-drop scheduling</h3>
        <p>In calendar view, grab any job card and drop it onto a different day or time slot to reschedule. The customer is notified automatically via SMS if the <code>reschedule_notification</code> template is enabled.</p>
        <h3>Job statuses (5 total)</h3>
        <ul>
          <li><strong>Scheduled</strong> — created, awaiting start. Default state.</li>
          <li><strong>In Progress</strong> — the tech tapped Start in the mobile app.</li>
          <li><strong>Completed</strong> — finished and ready to invoice.</li>
          <li><strong>Completed (Pending Payment)</strong> — done but unpaid; excluded from revenue totals until paid.</li>
          <li><strong>Cancelled</strong> — won&apos;t happen; archived but searchable. Hidden from the calendar by default.</li>
        </ul>
        <h3>Bulk reschedule</h3>
        <p>Select multiple jobs in list view (checkboxes), click <strong>Bulk Actions &gt; Reschedule</strong>, then pick a new date. Useful for weather days when you&apos;re pushing tomorrow&apos;s work to next week.</p>
        <h3>Calendar filters</h3>
        <p>Filter by technician, status, service type, or location (multi-location plans). Filters persist across sessions per browser.</p>
        <Tip>The Jobs API uses cursor pagination, so the page stays fast at 100k+ records. Don&apos;t worry about scrolling deep — the calendar lazy-loads as you navigate.</Tip>
      </>
    ),
  },
  {
    id: "customers",
    title: "Customers & CRM",
    body: (
      <>
        <p>Customers store everything about the people you serve and become the source of truth for jobs, invoices, and reports.</p>
        <h3>Adding a customer</h3>
        <Steps
          items={[
            <>From the Customers page, click <strong>+ New Customer</strong>.</>,
            <>Enter name, phone, email. Phone numbers are validated against E.164 — leading <code>+</code> and country code required for international.</>,
            <>Add at least one address. You can add more later.</>,
            <>Optionally add tags (VIP, Maintenance Plan, Repeat) — tags are searchable and can drive automations.</>,
          ]}
        />
        <h3>Multiple addresses</h3>
        <p>Customers can have unlimited service addresses (rental properties, multi-property owners, businesses with branches). When you create a job, the Address Picker shows their saved addresses; if there&apos;s only one, it&apos;s applied automatically with no extra clicks.</p>
        <h3>Tags</h3>
        <p>Tags are short labels you can attach to a customer. They appear next to the name everywhere in the app. Power users tag VIPs, customers under maintenance contracts, or by service type. Tags are filterable in CSV exports too.</p>
        <h3>Customer portal</h3>
        <p>Each customer optionally gets a self-serve portal at <code>/portal</code> where they can see their job history, view open invoices, and book new appointments. Toggle the portal on globally in <strong>Settings &gt; Customer Portal</strong>, or per-customer from their profile.</p>
        <h3>CSV import</h3>
        <p>Bulk import via <strong>Customers &gt; Import CSV</strong>. Match columns once; we remember the mapping for next time. The importer validates phone/email formats and skips duplicates.</p>
        <h3>Search</h3>
        <p>The customer search box covers name, phone, email, address, tags, and customer ID — all simultaneously, all live.</p>
        <Tip>If you&apos;re migrating from a spreadsheet or another CRM, get the CSV import right the first time. Adding 5,000 customers via CSV takes 90 seconds — adding them by hand takes hours.</Tip>
      </>
    ),
  },
  {
    id: "invoices",
    title: "Invoices & Estimates",
    body: (
      <>
        <p>Invoices and estimates share one document model — only the type changes. Both can be sent via SMS or email, paid online via Stripe, and exported to PDF.</p>
        <h3>Creating</h3>
        <Steps
          items={[
            <>From the Invoices page, click <strong>+ New Invoice</strong> or <strong>+ New Estimate</strong>.</>,
            <>Pick the customer. The header (business name, address, phone) is auto-filled from <strong>Settings &gt; Business</strong>.</>,
            <>Add line items: pick from your saved services or type a one-off. Adjust quantity, price, and tax rate per line.</>,
            <>Optionally add notes (visible on the document) and internal memos (visible only to your team).</>,
            <>Click <strong>Save Draft</strong> to come back later, or <strong>Send</strong> to email/SMS the document.</>,
          ]}
        />
        <h3>Estimate → Invoice conversion</h3>
        <p>When a customer accepts an estimate, click <strong>Convert to Invoice</strong> on the document. Line items, customer, and totals all carry over; the estimate is preserved alongside the new invoice for the audit trail.</p>
        <h3>Stripe payment link</h3>
        <p>Every invoice automatically gets a unique public payment link. Customers click, pay by card via Stripe Connect, and the invoice flips to Paid automatically — no account required for the customer. Funds land in your bank on the standard Stripe payout schedule (typically 2 business days).</p>
        <h3>Overdue handling</h3>
        <p>Invoices automatically flip to <em>Overdue</em> 1 day past their due date. The <code>invoice_overdue</code> SMS template (if enabled) fires once at the overdue mark and once per week after that. You can also schedule manual reminders.</p>
        <h3>PDF download</h3>
        <p>Click <strong>Download PDF</strong> on any invoice or estimate to get a print-ready PDF. The PDF mirrors what the customer sees in their email.</p>
        <Tip>Sending the invoice via the email button fires the <code>invoice_sent</code> SMS template too — so customers get an email AND a follow-up SMS with the same pay link. Don&apos;t double-send manually.</Tip>
      </>
    ),
  },
  {
    id: "phones",
    title: "Phone System",
    body: (
      <>
        <p>Buy real phone numbers, route inbound calls, send and receive SMS, and record conversations — all powered by Twilio under the hood.</p>
        <h3>Buying numbers</h3>
        <Steps
          items={[
            <>Go to <strong>Settings &gt; Phones</strong> and click <strong>Buy Number</strong>.</>,
            <>Search by area code or city. Pick from the list — activated within seconds.</>,
            <>Numbers are billed monthly as part of your subscription&apos;s dedicated-line allotment. Extra lines beyond your plan are added per-line.</>,
          ]}
        />
        <h3>4 routing modes</h3>
        <ul>
          <li><strong>Forward</strong> — ring straight to a personal phone or another business number.</li>
          <li><strong>Voice Client</strong> — ring through to the in-app dialer in your browser. Best for office staff who want to answer from a desktop.</li>
          <li><strong>IVR</strong> — &quot;Press 1 for sales, 2 for support&quot; with multiple branches and after-hours fallback. Fully configurable in the IVR builder.</li>
          <li><strong>Voicemail</strong> — go straight to voicemail with optional transcription.</li>
        </ul>
        <h3>Call recording</h3>
        <p>Toggle <strong>Call Recording</strong> in the Phone Settings modal for any number. Inbound calls are recorded automatically and the recording appears inline on the Call History row — play, scrub, or download right from the list. Recordings are stored alongside the call log and only show on calls that actually have audio.</p>
        <h3>Voicemail transcription</h3>
        <p>Enable <strong>Transcribe Voicemail</strong> on any number. New voicemails are transcribed within ~30 seconds and the text appears alongside the audio in your inbox. Transcriptions also flow into the call log search.</p>
        <Tip>Recording disclosure laws vary by state. Many states require two-party consent — make sure your IVR or greeting includes a notice (&quot;This call may be recorded for quality...&quot;) if you record.</Tip>
      </>
    ),
  },
  {
    id: "call-masking",
    title: "Call Masking",
    body: (
      <>
        <p>Lets technicians call customers through your business number — without revealing their personal cell. Customers see your business number on caller ID; the tech&apos;s phone stays private.</p>
        <h3>How the PIN bridge works</h3>
        <Steps
          items={[
            <>When a job is assigned to a tech, Fieldz generates a unique 4-digit PIN tied to that job + customer pair.</>,
            <>The tech opens the job in the mobile app and taps <strong>Call</strong>. The app dials your masked Twilio number and auto-enters the PIN.</>,
            <>Twilio looks up the PIN, validates it&apos;s still active, and bridges the call to the customer&apos;s number — showing your business caller ID.</>,
            <>The whole call is logged on the customer&apos;s job page (and recorded if you have recording enabled).</>,
          ]}
        />
        <h3>Enabling masking per-job</h3>
        <p>Masking is automatic on every assigned job — no manual flip. Each job/tech pair gets its own PIN.</p>
        <h3>PIN expiry</h3>
        <p>PINs expire automatically when the job is marked <em>Completed</em> or <em>Cancelled</em>, so old PINs can&apos;t be reused. You can also revoke a PIN manually from the masked-calls log if needed.</p>
        <Tip>If a customer calls back the masked number after the job is closed, the call falls through to your default IVR/voicemail — no orphaned PINs leaking out, no dead-end ring.</Tip>
      </>
    ),
  },
  {
    id: "sms-automations",
    title: "SMS & Automations",
    body: (
      <>
        <p>Send and receive SMS from your business number, and trigger templated messages automatically when key events happen.</p>
        <h3>6 built-in trigger events</h3>
        <DocsTable
          headers={["Event", "Fires when…"]}
          rows={[
            [<code>job_scheduled</code>, "A new job is created with a customer"],
            [<code>job_reminder</code>, "N hours before a scheduled job (configurable per template)"],
            [<code>quote_sent</code>, "An estimate transitions to Sent (status endpoint OR email send button)"],
            [<code>invoice_sent</code>, "An invoice is sent (status endpoint OR email send button)"],
            [<code>invoice_paid</code>, "An invoice is marked paid (manual OR Stripe webhook)"],
            [<code>review_request</code>, "N days after a job completes (configurable per template)"],
          ]}
          testid="trigger-events-table"
        />
        <h3>Configuring a template</h3>
        <Steps
          items={[
            <>Go to <strong>Settings &gt; SMS Templates</strong>.</>,
            <>Click <strong>+ New Template</strong> and pick a trigger event from the dropdown.</>,
            <>Compose the message body using placeholders (see below).</>,
            <>Toggle the template <strong>Active</strong>. From now on, every matching event sends the message via your business number.</>,
          ]}
        />
        <h3>Placeholders</h3>
        <p>Use these in your template body — they&apos;re replaced with the live value at send time:</p>
        <ul>
          <li><code>{"{{customer_name}}"}</code> — the customer&apos;s full name</li>
          <li><code>{"{{business_name}}"}</code> — your company name</li>
          <li><code>{"{{business_phone}}"}</code> — your business phone</li>
          <li><code>{"{{quote_amount}}"}</code> — estimate total (formatted with $ and 2 decimals)</li>
          <li><code>{"{{invoice_amount}}"}</code> — invoice total</li>
          <li><code>{"{{balance_due}}"}</code> — outstanding balance</li>
          <li><code>{"{{pay_link}}"}</code> — public Stripe payment link</li>
          <li><code>{"{{invoice_number}}"}</code> — your invoice number (e.g. INV-001)</li>
          <li><code>{"{{job_type}}"}</code> — the service type name</li>
        </ul>
        <h3>STOP/HELP compliance</h3>
        <p>US carrier rules require every SMS sender to honor STOP/HELP keywords. We append the required disclosure (&quot;Reply STOP to opt out&quot;) on the first message of every customer thread automatically and process STOP/UNSUBSCRIBE replies for you — no manual handling needed.</p>
        <Tip>Keep templates under 160 characters where possible. Longer messages are split into multiple billable segments by the carrier; you&apos;ll burn through your SMS allotment faster than expected.</Tip>
      </>
    ),
  },
  {
    id: "team",
    title: "Team & Permissions",
    body: (
      <>
        <p>Add technicians, dispatchers, and admins. Each has their own login, permissions, and (for techs) a dedicated mobile experience.</p>
        <h3>Permission matrix</h3>
        <DocsTable
          headers={["Capability", "Owner", "Admin", "Dispatcher", "Technician"]}
          rows={[
            ["View jobs / customers", "✓", "✓", "✓", "Own jobs only"],
            ["Create / edit jobs", "✓", "✓", "✓", "—"],
            ["Send invoices", "✓", "✓", "✓", "Own jobs only"],
            ["Edit team members", "✓", "✓", "—", "—"],
            ["Edit settings", "✓", "✓", "—", "—"],
            ["Manage subscription / billing", "✓", "—", "—", "—"],
            ["Delete account", "✓", "—", "—", "—"],
            ["Mobile app access", "—", "—", "—", "✓"],
          ]}
          testid="permissions-matrix"
        />
        <h3>Invite flow</h3>
        <Steps
          items={[
            <>Go to <strong>Settings &gt; Team</strong> and click <strong>+ Invite Member</strong>.</>,
            <>Enter their name, email, phone, and pick a role.</>,
            <>The invitee gets an SMS with a setup link (valid 7 days). Setup is name-confirm + password.</>,
            <>Once they create their password, they&apos;re Active on your account.</>,
          ]}
        />
        <h3>Deactivating members</h3>
        <p>Open the team member row and click <strong>Deactivate</strong>. Their past jobs remain attributed to them for accurate revenue/commission reports — you don&apos;t lose history.</p>
        <Tip>You can&apos;t demote yourself from Owner if you&apos;re the only Owner on the account. Promote someone else to Owner first if you want to step down.</Tip>
      </>
    ),
  },
  {
    id: "multi-location",
    title: "Multi-Location",
    body: (
      <>
        <p>If you run more than one branch — different cities, brands, or service lines under one parent — multi-location plans give you one dashboard for all of them with full role-scoping per location.</p>
        <h3>Highlights</h3>
        <ul>
          <li><strong>Centralized dispatch</strong> — route jobs to the closest location, or hand-pick a tech across any branch.</li>
          <li><strong>Per-location phone numbers</strong> — each branch has its own customer-facing line, with shared inbox at the parent level.</li>
          <li><strong>Unified reporting</strong> — revenue, technician performance, and inventory rolled up into one consolidated view.</li>
          <li><strong>Role-scoped access</strong> — branch managers see only their branch; the parent owner sees everything.</li>
          <li><strong>Single billing</strong> — one subscription covers every branch; no per-location fees.</li>
        </ul>
        <h3>Branch setup</h3>
        <Steps
          items={[
            <>From <strong>Settings &gt; Locations</strong>, click <strong>+ New Location</strong>.</>,
            <>Add the branch name, address, and contact phone.</>,
            <>Optionally assign a Location Manager (their role gets scoped automatically to just that branch&apos;s data).</>,
            <>Buy a phone number specifically for this location and assign it under <strong>Settings &gt; Phones</strong>.</>,
          ]}
        />
        <h3>Pricing</h3>
        <p>Plans start with up to 3 locations and scale to unlimited. See <Link to="/#multi-location">/#multi-location</Link> on the marketing site for current pricing and limits.</p>
        <Tip>Locations don&apos;t have to be physical addresses. Many users create &quot;virtual&quot; locations for separate brands or service lines (e.g., Residential vs Commercial) running through one parent account.</Tip>
      </>
    ),
  },
  {
    id: "reports",
    title: "Reports & Dashboard",
    body: (
      <>
        <p>The Dashboard is your daily home. Reports go deeper for monthly/quarterly review.</p>
        <h3>Revenue widget</h3>
        <p>Shows month-to-date revenue, completed-this-week count, and outstanding invoice total. Click any number to drill into the underlying job/invoice list.</p>
        <h3>Date range presets</h3>
        <p>Quick filters: <em>Today</em>, <em>This Week</em>, <em>This Month</em>, <em>This Quarter</em>, <em>This Year</em>, plus a custom range picker. Numbers exclude jobs in <em>Pending Payment</em> status — those only count once paid.</p>
        <h3>Activity feed</h3>
        <p>Real-time feed of what your team did most recently: invoice paid, customer added, call answered, jobs completed. Useful for keeping a pulse on the day without checking 5 different pages.</p>
        <h3>Quick actions</h3>
        <p>The three buttons at the top of the dashboard — <strong>+ New Job</strong>, <strong>+ New Customer</strong>, <strong>Send Invoice</strong> — cover ~80% of what owners and dispatchers do day-to-day.</p>
        <Tip>The header notification bell shows missed calls and unread SMS even when you&apos;re on a different page. If you only check one spot per hour, make it the bell.</Tip>
      </>
    ),
  },
];

const DocsFeaturesPage = () => (
  <DocsCategoryLayout
    category={{
      id: "features",
      name: "Features",
      icon: SettingsIcon,
      intro: "Deep dives on every feature in Fieldz AI. Each article covers what it does, when to use it, and how to set it up.",
    }}
    articles={ARTICLES}
  />
);

export default DocsFeaturesPage;
